import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { BottomSheet } from '@/shared/components/BottomSheet'
import { useRecipes } from '@/features/recipes/useRecipes'
import { RecipeSearchInput } from '@/features/recipes/RecipeSearchInput'

interface RecipePickerProps {
  open: boolean
  onClose: () => void
  onSelect: (recipeId: string) => void
}

export function RecipePicker({ open, onClose, onSelect }: RecipePickerProps) {
  const { t } = useTranslation('meal-plan')
  const { t: tRecipes } = useTranslation('recipes')
  const { data: recipes = [] } = useRecipes()
  const [searchQuery, setSearchQuery] = useState('')

  const { favourites, others } = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase()
    const filtered = searchQuery
      ? recipes.filter((r) => r.name.toLowerCase().includes(lowerQuery))
      : recipes

    return {
      favourites: filtered.filter((r) => r.isFavourite),
      others: filtered.filter((r) => !r.isFavourite),
    }
  }, [recipes, searchQuery])

  return (
    <BottomSheet open={open} onClose={onClose} title={t('picker.title')}>
      <div className="p-4 flex flex-col gap-4">
        {recipes.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-muted mb-3">{t('picker.emptyLibrary')}</p>
            <Link
              to="/recipes"
              onClick={onClose}
              className="text-accent underline text-sm"
            >
              {t('picker.goToRecipes')}
            </Link>
          </div>
        ) : (
          <>
            <RecipeSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={tRecipes('search.placeholder')}
            />
            {favourites.length === 0 && others.length === 0 && searchQuery ? (
              <div className="py-8 text-center">
                <p className="text-muted text-sm">
                  {tRecipes('search.noResults', { query: searchQuery })}
                </p>
              </div>
            ) : (
              <>
                {favourites.length > 0 && (
                  <section>
                    <h2 className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                      {t('picker.favouritesHeading')}
                    </h2>
                    <ul className="flex flex-col gap-1">
                      {favourites.map((recipe) => (
                        <li key={recipe.id}>
                          <button
                            type="button"
                            aria-label={t('picker.recipeItemAriaLabel', { name: recipe.name })}
                            onClick={() => onSelect(recipe.id)}
                            className="w-full min-h-[44px] text-left px-3 py-2 rounded-lg hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <span className="text-sm font-medium text-ink">{recipe.name}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {others.length > 0 && (
                  <section>
                    <h2 className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                      {t('picker.allRecipesHeading')}
                    </h2>
                    <ul className="flex flex-col gap-1">
                      {others.map((recipe) => (
                        <li key={recipe.id}>
                          <button
                            type="button"
                            aria-label={t('picker.recipeItemAriaLabel', { name: recipe.name })}
                            onClick={() => onSelect(recipe.id)}
                            className="w-full min-h-[44px] text-left px-3 py-2 rounded-lg hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <span className="text-sm font-medium text-ink">{recipe.name}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  )
}
